import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../shared/services/auth/auth.service';
import { ConfigService } from '../../shared/services/config.service';

export interface ApiOperation {
  id: string;
  name: string;
  displayName?: string;
  path: string;
  method: string;
  description: string;
  instructionUrl: string;
  version: string;
  parameters: ApiParameter[];
  applicationId: string;
  applicationName: string;
}

export interface ApiParameter {
  name: string;
  type: string;
  required: boolean;
}

export interface ApiOperationsResponse {
  data: ApiOperation[];
  continuationToken?: string;
}

export interface DiscoverabilityConfig {
  baseUrl: string;
  subscriptionKey: string;
  version: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiDiscoverabilityService {
  private readonly config: DiscoverabilityConfig;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private configService: ConfigService
  ) {
    // Use environment-specific configuration
    const appConfig = this.configService.getConfig();
    this.config = appConfig.discoverabilityConfig;
    
    if (!this.config) {
      throw new Error('discoverabilityConfig is not defined in configuration');
    }
  }

  /**
   * Create HTTP headers with Authorization and subscription key
   */
  private async createHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders({
      'X-Subscription-Key': this.config.subscriptionKey,
      'Content-Type': 'application/json'
    });

    try {
      // Get access token from auth service
      const accessToken = await firstValueFrom(this.authService.getAccessToken());
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`);
      }
    } catch (error) {
      console.warn('Failed to get access token for API discoverability request:', error);
      // Continue without Authorization header - some endpoints might work without it
    }

    return headers;
  }

  /**
   * Get all API operations from the discoverability service
   */
  getApiOperations(continuationToken?: string): Observable<ApiOperationsResponse> {
    let params = new HttpParams();
    if (continuationToken) {
      params = params.set('continuationToken', continuationToken);
    }

    return new Observable(observer => {
      this.createHeaders().then(headers => {
        const url = `${this.config.baseUrl}/${this.config.version}/operations`;
        
        this.http.get<ApiOperationsResponse>(url, { headers, params })
          .pipe(
            catchError(error => {
              console.error('Error fetching API operations:', error);
              return throwError(() => new Error('Failed to fetch API operations from discoverability service'));
            })
          )
          .subscribe(observer);
      }).catch(error => {
        observer.error(new Error('Failed to create request headers'));
      });
    });
  }

  /**
   * Get API operations by application name
   */
  getApiOperationsByApplication(applicationName: string, continuationToken?: string): Observable<ApiOperationsResponse> {
    let params = new HttpParams();
    if (continuationToken) {
      params = params.set('continuationToken', continuationToken);
    }

    return new Observable(observer => {
      this.createHeaders().then(headers => {
        const url = `${this.config.baseUrl}/${this.config.version}/applications/${applicationName}/operations`;
        
        this.http.get<ApiOperationsResponse>(url, { headers, params })
          .pipe(
            catchError(error => {
              console.error(`Error fetching API operations for ${applicationName}:`, error);
              return throwError(() => new Error(`Failed to fetch API operations for ${applicationName}`));
            })
          )
          .subscribe(observer);
      }).catch(error => {
        observer.error(new Error('Failed to create request headers'));
      });
    });
  }

  /**
   * Get specific API operation by ID
   */
  getApiOperation(operationId: string): Observable<ApiOperation> {
    return new Observable(observer => {
      this.createHeaders().then(headers => {
        const url = `${this.config.baseUrl}/${this.config.version}/operations/${operationId}`;
        
        this.http.get<{ data: ApiOperation }>(url, { headers })
          .pipe(
            map(response => response.data),
            catchError(error => {
              console.error(`Error fetching API operation ${operationId}:`, error);
              return throwError(() => new Error(`Failed to fetch API operation ${operationId}`));
            })
          )
          .subscribe(observer);
      }).catch(error => {
        observer.error(new Error('Failed to create request headers'));
      });
    });
  }

  /**
   * Search API operations by name (client-side filtering)
   */
  searchApiOperations(searchTerm: string): Observable<ApiOperation[]> {
    return this.getApiOperations().pipe(
      map(response => response.data.filter(operation => 
        operation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.applicationName.toLowerCase().includes(searchTerm.toLowerCase())
      ))
    );
  }
}